import { Link } from "@tanstack/react-router";
import { ArrowDown, ArrowUp, SlidersHorizontal, TriangleAlert, Users } from "lucide-react";
import { useMemo, useState } from "react";
import { useSetBreadcrumbs } from "@/components/shared/breadcrumbs";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { FilterRow } from "@/components/shared/filter-bar";
import { FilterPanel } from "@/components/shared/filter-panel";
import { FilterToggleGroup } from "@/components/shared/filter-toggle-group";
import { LoadingRows } from "@/components/shared/loading-rows";
import { useOriginLinkSearch } from "@/components/shared/origin-context";
import { PageHeader } from "@/components/shared/page-header";
import {
  RatingBadge,
  RatingDistributionBar,
  ratingColorVar,
} from "@/components/shared/rating-scale";
import { SearchInput } from "@/components/shared/search-input";
import { StatusPill } from "@/components/shared/status-badge";
import { TabBar, type TabItem } from "@/components/shared/tab-bar";
import { type SortState, useTableSort } from "@/components/shared/use-table-sort";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  type ConstraintDirectoryEntry,
  DEPTH_THRESHOLD,
  useConstraintDirectory,
  useConstraintHolders,
} from "@/hooks/use-constraint-directory";
import { useConstraintLevels, useConstraintTypes } from "@/hooks/use-constraints";

/**
 * A gap is a constraint people HOLD but where nobody reaches
 * {@link DEPTH_THRESHOLD} — the claim exists and the depth doesn't.
 *
 * **Constraints nobody holds are deliberately NOT gaps.** They were, briefly,
 * on the reasoning that both mean "can't staff it" — but on real data that made
 * 59 of 68 constraints a gap, because most of the list has never been rated.
 * "Nobody has rated this yet" and "people claim this but none of them is
 * strong" are different problems with different fixes, and only the second is
 * actionable from this page; the "constraints in use" tile carries the first.
 *
 * One predicate, shared by the filter, the badge and the API's summary count —
 * they disagreed once already.
 */
function isGap(entry: ConstraintDirectoryEntry): boolean {
  return entry.holderCount > 0 && entry.depthCount === 0;
}

/**
 * The **constraints directory** (WI-140) — "who is familiar with X, and how
 * well".
 *
 * This route used to be a disabled placeholder pretending to be the pick-list
 * editor. Now that authoring lives at Settings → Config → Constraints alongside
 * every other reference list, the page is free to be what the story actually
 * asked for. It is deliberately a SEPARATE page from the People directory: this
 * is a staffing question asked constraint-first, and folding it in as another
 * person filter buries it behind every other field.
 *
 * Master/detail, like the Capacity report. Picking a constraint lists its
 * holders — which is what makes "only people who actually hold a constraint
 * appear for it" true by construction rather than by a filter someone has to
 * remember to apply.
 *
 * **Admin tier only** since 2026-09-03. It was first built open, with ratings
 * redacted per viewer — correct, but it meant the rating filter could only ever
 * range over the people a given viewer already had access to, so the same
 * filter behaved differently depending on who was looking. Restricting the
 * report removes that surprise, and moved it into Administration beside the
 * other company-wide reports.
 *
 * The server-side redaction and the scoped filter are KEPT rather than unwound.
 * They cost nothing while every viewer is an admin, and they are what stops
 * this becoming a disclosure the day someone widens the gate. Never re-implement
 * the rating filter on the client: narrowing on a value the viewer cannot see is
 * precisely how a filter discloses that value.
 */
export function ConstraintsPage() {
  // Default ON: a quarter of the ratings belong to people who have left, so
  // counting them overstates the bench everywhere on this page.
  const [activeOnly, setActiveOnly] = useState(true);
  const { data, isLoading, isError, error, refetch } = useConstraintDirectory(!activeOnly);
  const rows = useMemo(() => data?.constraints ?? [], [data]);
  const summary = data?.summary;
  const { data: levels = [] } = useConstraintLevels();
  const [search, setSearch] = useState("");
  const [activeTypeId, setActiveTypeId] = useState<string | null>(null);
  // Same toggle semantics as every directory table in the app. Defaults to
  // most-held-first: on a REPORT that is the useful order, and it subsumes
  // the special case that used to sink unheld constraints to the bottom —
  // with people descending they land there on their own.
  const { sort, toggleSort } = useTableSort("people", "desc");
  const [ratings, setRatings] = useState<string[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [gapsOnly, setGapsOnly] = useState(false);

  useSetBreadcrumbs([{ label: "Administration" }, { label: "Constraints" }]);

  // Types come from the config endpoint rather than being derived from the
  // constraints, so a grouping with nothing in it still gets a tab — "ICP · 0"
  // tells you it is unpopulated, which deriving from rows would hide.
  const { data: allTypes = [] } = useConstraintTypes();
  const tabTypes = useMemo(() => allTypes.filter((t) => t.isActive), [allTypes]);

  const levelNames = useMemo(() => new Map(levels.map((l) => [l.ordinal, l.name])), [levels]);

  const ratingOptions = useMemo(
    () => levels.map((l) => ({ value: String(l.ordinal), label: `${l.ordinal} — ${l.name}` })),
    [levels],
  );

  // Everything matching the list-level filters, across every type. The tab
  // counts read off this, so searching while on one tab shows you which OTHER
  // tab holds the hit rather than leaving you to conclude there isn't one.
  const matching = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (gapsOnly && !isGap(r)) return false;
      if (!q) return true;
      return r.name.toLowerCase().includes(q) || r.typeName.toLowerCase().includes(q);
    });
  }, [rows, search, gapsOnly]);

  const tabs: TabItem[] = useMemo(
    () =>
      tabTypes.map((t) => ({
        key: t.key,
        label: `${t.name} · ${matching.filter((r) => r.typeKey === t.key).length}`,
      })),
    [tabTypes, matching],
  );

  // Default to the first type in the admin's configured order — they control
  // that ordering on the config page, so it is the configurable answer rather
  // than a hard-coded favourite. Falls back if the active one disappears.
  const activeType = tabTypes.find((t) => t.key === activeTypeId)?.key ?? tabTypes[0]?.key ?? null;

  const visible = useMemo(() => {
    const inTab = matching.filter((r) => r.typeKey === activeType);
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...inTab].sort((a, b) => {
      if (sort.key === "name") return dir * a.name.localeCompare(b.name);
      // Ties on the count break by name rather than falling back to input
      // order, so the list is stable and equal rows don't swap on a refetch.
      return dir * (a.holderCount - b.holderCount) || a.name.localeCompare(b.name);
    });
  }, [matching, activeType, sort]);

  const selected = rows.find((r) => r.id === selectedId) ?? null;
  const levelFilter = useMemo(() => ratings.map(Number), [ratings]);

  const filterSummary: string[] = [];
  if (ratings.length) filterSummary.push(`Rating: ${ratings.join(", ")}`);
  if (gapsOnly) filterSummary.push("Gaps only");
  if (!activeOnly) filterSummary.push("Including people who have left");

  if (isError) {
    return (
      <ErrorState
        message={error?.message ?? "Failed to load the constraints directory"}
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Administration"
        title="Constraints"
        description="Who is familiar with which industries, technologies and kinds of client — and where we're thin. Pick a constraint to see its coverage and the people who hold it."
      />

      {/* Said plainly rather than left to be inferred from short lists. Without
          NOS we can only show what people already hold, so "of N configured" is
          not the vocabulary — it is the subset somebody happens to be rated on. */}
      {summary && !summary.connected && (
        <div className="rounded-md border border-border bg-muted/40 px-4 py-3 text-sm text-muted-foreground">
          <span className="font-medium text-foreground">Not connected to NOS.</span> The full
          constraint list lives in NOS and can't be read right now, so this shows only the
          constraints people are already rated on.
        </div>
      )}

      {summary && (
        <dl className="grid gap-3 sm:grid-cols-3">
          <SummaryTile
            label="Constraints in use"
            value={summary.inUse}
            hint={summary.connected ? `of ${summary.total} configured` : "NOS unavailable"}
          />
          <SummaryTile
            label="People rated"
            value={summary.peopleRated}
            hint={activeOnly ? "active people only" : "including people who have left"}
          />
          <SummaryTile
            label="Gaps"
            value={summary.gaps}
            hint={`held, but nobody rated ${DEPTH_THRESHOLD} or above`}
            tone={summary.gaps > 0 ? "warn" : "plain"}
          />
        </dl>
      )}

      <FilterPanel
        activeCount={ratings.length + (gapsOnly ? 1 : 0) + (activeOnly ? 0 : 1)}
        summary={filterSummary}
      >
        <FilterRow label="Rating">
          <FilterToggleGroup
            options={ratingOptions}
            value={ratings}
            onChange={setRatings}
            allLabel="Any rating"
          />
        </FilterRow>
        <FilterRow label="People">
          <div className="flex items-center gap-2 text-sm">
            <Checkbox
              id="constraints-active-only"
              checked={activeOnly}
              onCheckedChange={(v) => setActiveOnly(v === true)}
            />
            <label htmlFor="constraints-active-only" className="cursor-pointer select-none">
              Only count active people
            </label>
          </div>
        </FilterRow>
        <FilterRow label="Coverage">
          <div className="flex items-center gap-2 text-sm">
            <Checkbox
              id="constraints-gaps-only"
              checked={gapsOnly}
              onCheckedChange={(v) => setGapsOnly(v === true)}
            />
            <label htmlFor="constraints-gaps-only" className="cursor-pointer select-none">
              Only show gaps — held, but nobody rated {DEPTH_THRESHOLD} or above
            </label>
          </div>
        </FilterRow>
      </FilterPanel>

      {/* The tab strip is page-level, not inside the left column: three type
          names don't fit in 20rem. Counts respect the search and the gaps
          filter, so searching on one tab shows which OTHER tab holds the hit. */}
      {tabs.length > 0 && activeType && (
        <TabBar
          tabs={tabs}
          value={activeType}
          onChange={(key) => {
            setActiveTypeId(key);
            // The selected constraint belongs to the tab you left, so drop it
            // and show the new tab's overview rather than a detail panel that
            // no longer matches the list beside it.
            setSelectedId(null);
          }}
        />
      )}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,20rem)_minmax(0,1fr)]">
        <div className="space-y-3">
          <SearchInput value={search} onValueChange={setSearch} placeholder="Search constraints…" />
          {isLoading ? (
            <LoadingRows />
          ) : visible.length === 0 ? (
            <EmptyState
              icon={SlidersHorizontal}
              title={gapsOnly ? "No gaps" : "No constraints"}
              description={
                gapsOnly
                  ? `Every constraint anyone holds has someone rated ${DEPTH_THRESHOLD} or above.`
                  : "Constraints are set up at Settings → Config → Constraints."
              }
            />
          ) : (
            <div className="overflow-hidden rounded-md border border-border">
              <div className="flex items-center gap-2 border-border border-b bg-muted/40 px-3 py-1.5">
                <SortHeader
                  label="Name"
                  sortKey="name"
                  sort={sort}
                  onSort={toggleSort}
                  className="min-w-0 flex-1"
                />
                <SortHeader label="People" sortKey="people" sort={sort} onSort={toggleSort} />
              </div>
              <ul>
                {visible.map((row) => (
                  <li key={row.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(row.id)}
                      aria-current={row.id === selectedId}
                      className={`flex w-full items-center justify-between gap-2 border-border border-b px-3 py-2 text-left text-sm transition-colors last:border-0 hover:bg-muted/50 ${
                        row.id === selectedId ? "bg-muted font-medium" : ""
                      }`}
                    >
                      <span className="flex min-w-0 items-center gap-2">
                        <span className="truncate">{row.name}</span>
                        {/* Two different facts, not one: hidden is a decision
                            Enable made, missing is NOS having dropped the value.
                            Only the second means nobody can act on it. */}
                        {row.isHidden && <StatusPill tone="neutral" label="Hidden" />}
                        {row.isMissingFromNos && <StatusPill tone="neutral" label="Not in NOS" />}
                      </span>
                      <span className="flex shrink-0 items-center gap-2">
                        {/* The roster count alone hides the gap: three
                            people "familiar" reads very differently when
                            all three are 1s. The bar shows the shape
                            without a click; `isGap` flags the worst case. */}
                        <RatingDistributionBar
                          levelCounts={row.levelCounts}
                          levelNames={levelNames}
                          total={row.holderCount}
                        />
                        {isGap(row) && (
                          <TriangleAlert
                            className="size-3.5 text-warning-foreground"
                            aria-label={`Nobody rated ${DEPTH_THRESHOLD} or above`}
                          />
                        )}
                        <span className="w-5 text-right text-muted-foreground text-xs tabular-nums">
                          {row.holderCount}
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div>
          {selected ? (
            <HoldersPanel
              constraint={selected}
              levels={levelFilter}
              includeInactive={!activeOnly}
              onClearRatings={() => setRatings([])}
            />
          ) : (
            <DirectoryOverview
              constraints={visible}
              levelNames={levelNames}
              onSelect={setSelectedId}
            />
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Coverage for one constraint (WI-141) — how many people sit at each rung.
 *
 * **This answers the gap question, which the holder count alone cannot.** Three
 * people "familiar" with a domain is a very different fact when all three are
 * 1s, and that difference is what decides whether we can sell into it.
 *
 * Bars scale to the largest rung on THIS constraint, so the shape of one
 * distribution is readable; they are not comparable between constraints, which
 * is why every bar is labelled with its own count rather than read off a size.
 * Aggregate only — the counts come from a grouped query that selects no person.
 *
 * Deliberately unlike the skills drill-down (AC-369): that reads as an
 * expectation met or missed, this is a distribution across a familiarity scale,
 * with no notion of a required level.
 */
/**
 * A sort toggle for the master list's header strip.
 *
 * The list is a `<ul>` of selectable rows rather than a `<table>`, so it can't
 * use `DataTable`'s sortable headers — but it reuses `useTableSort`, so the
 * click-to-flip behaviour and the arrow affordance match every directory table
 * in the app rather than being a second convention.
 */
function SortHeader({
  label,
  sortKey,
  sort,
  onSort,
  className = "",
}: {
  label: string;
  sortKey: string;
  sort: SortState;
  onSort: (key: string) => void;
  className?: string;
}) {
  const active = sort.key === sortKey;
  const Arrow = sort.dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <button
      type="button"
      onClick={() => onSort(sortKey)}
      // The arrow carries the state visually; this carries it to a screen
      // reader. `aria-sort` would be the right attribute but it belongs on a
      // columnheader, and this list is a <ul> of selectable rows, not a table.
      aria-label={
        active
          ? `${label}, sorted ${sort.dir === "asc" ? "ascending" : "descending"}. Activate to reverse.`
          : `Sort by ${label.toLowerCase()}`
      }
      className={`flex items-center gap-1 font-medium text-xs uppercase tracking-wide transition-colors ${
        active ? "text-foreground" : "text-muted-foreground hover:text-foreground"
      } ${className}`}
    >
      {label}
      {active && <Arrow className="size-3" aria-hidden />}
    </button>
  );
}

/**
 * One headline number. A stat tile rather than a chart: three magnitudes with
 * no comparison between them is exactly the case where a chart adds decoration
 * and no meaning.
 */
function SummaryTile({
  label,
  value,
  hint,
  tone = "plain",
}: {
  label: string;
  value: number;
  hint: string;
  tone?: "plain" | "warn";
}) {
  return (
    <div className="rounded-md border border-border px-4 py-3">
      <dt className="text-muted-foreground text-xs uppercase tracking-wide">{label}</dt>
      <dd
        className={`font-semibold text-2xl tabular-nums ${
          tone === "warn" ? "text-warning-foreground" : ""
        }`}
      >
        {value}
      </dd>
      <p className="text-muted-foreground text-xs">{hint}</p>
    </div>
  );
}

/**
 * What the right-hand panel shows before anything is selected.
 *
 * Previously an empty "pick a constraint" prompt, which made the landing state
 * the one screen that answered nothing. Leads with the gaps, because "where are
 * we thin" is the question this report exists for, and the strongest areas are
 * the reassuring half of the same answer.
 *
 * Scoped to the SELECTED TAB, not the whole directory: with the type tabs in
 * place everything else on screen belongs to one grouping, and a cross-type
 * overview would send you to a constraint the list beside it isn't showing.
 * The summary tiles above stay global, so the whole-org picture is still there.
 */
function DirectoryOverview({
  constraints,
  levelNames,
  onSelect,
}: {
  constraints: ConstraintDirectoryEntry[];
  levelNames: Map<number, string>;
  onSelect: (id: string) => void;
}) {
  const held = constraints.filter((c) => c.holderCount > 0);
  const gaps = held.filter((c) => c.depthCount === 0).slice(0, 6);
  const strongest = [...held]
    .filter((c) => c.depthCount > 0)
    .sort((a, b) => b.depthCount - a.depthCount || b.holderCount - a.holderCount)
    .slice(0, 6);

  if (held.length === 0) {
    return (
      <div className="rounded-md border border-border border-dashed p-10">
        <EmptyState
          icon={Users}
          title="Nobody is rated yet"
          description="Ratings are added on a person's profile, under Constraints."
        />
      </div>
    );
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <OverviewColumn
        title="Thinnest coverage"
        blurb={`Held, but nobody rated ${DEPTH_THRESHOLD} or above.`}
        rows={gaps}
        levelNames={levelNames}
        onSelect={onSelect}
        empty="Every constraint has someone at depth."
      />
      <OverviewColumn
        title="Deepest bench"
        blurb={`Most people rated ${DEPTH_THRESHOLD} or above.`}
        rows={strongest}
        levelNames={levelNames}
        onSelect={onSelect}
        empty="Nobody is rated at depth yet."
      />
    </div>
  );
}

function OverviewColumn({
  title,
  blurb,
  rows,
  levelNames,
  onSelect,
  empty,
}: {
  title: string;
  blurb: string;
  rows: ConstraintDirectoryEntry[];
  levelNames: Map<number, string>;
  onSelect: (id: string) => void;
  empty: string;
}) {
  return (
    <div className="rounded-md border border-border p-4">
      <h2 className="font-semibold text-sm">{title}</h2>
      <p className="mb-3 text-muted-foreground text-xs">{blurb}</p>
      {rows.length === 0 ? (
        <p className="text-muted-foreground text-sm">{empty}</p>
      ) : (
        <ul className="divide-y divide-border">
          {rows.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                onClick={() => onSelect(c.id)}
                className="flex w-full items-center gap-2 py-2 text-left text-sm hover:underline"
              >
                <span className="min-w-0 flex-1 truncate">{c.name}</span>
                <RatingDistributionBar
                  levelCounts={c.levelCounts}
                  levelNames={levelNames}
                  total={c.holderCount}
                />
                <span className="w-5 shrink-0 text-right text-muted-foreground text-xs tabular-nums">
                  {c.holderCount}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CoverageBars({ constraint }: { constraint: ConstraintDirectoryEntry }) {
  const { data: levels = [] } = useConstraintLevels();
  if (constraint.holderCount === 0) return null;

  const peak = Math.max(...Object.values(constraint.levelCounts), 1);
  const noDepth = constraint.depthCount === 0;

  return (
    <div className="space-y-2 rounded-md bg-muted/40 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-medium text-xs uppercase tracking-wide">Coverage</span>
        <span
          className={`text-xs ${noDepth ? "text-warning-foreground" : "text-muted-foreground"}`}
        >
          {noDepth ? (
            <span className="inline-flex items-center gap-1">
              <TriangleAlert className="size-3.5" />
              Nobody rated {DEPTH_THRESHOLD} or above
            </span>
          ) : (
            <>
              {constraint.depthCount} at {DEPTH_THRESHOLD} or above
            </>
          )}
        </span>
      </div>
      <ul className="space-y-1">
        {/* Highest rung first, and EVERY rung is listed: an empty one is the
            informative row, so a missing count renders as a zero, not a gap in
            the list. */}
        {[...levels]
          .sort((a, b) => b.ordinal - a.ordinal)
          .map((rung) => {
            const count = constraint.levelCounts[rung.ordinal] ?? 0;
            return (
              <li key={rung.id} className="flex items-center gap-2 text-xs">
                <span className="w-4 shrink-0 text-right tabular-nums">{rung.ordinal}</span>
                <span className="w-36 shrink-0 truncate text-muted-foreground">{rung.name}</span>
                <span className="h-2 min-w-0 flex-1 overflow-hidden rounded-full bg-border">
                  <span
                    className="block h-full rounded-full"
                    style={{
                      width: `${(count / peak) * 100}%`,
                      backgroundColor: ratingColorVar(rung.ordinal),
                    }}
                  />
                </span>
                <span className="w-6 shrink-0 text-right tabular-nums">{count}</span>
              </li>
            );
          })}
      </ul>
    </div>
  );
}

function HoldersPanel({
  constraint,
  levels,
  includeInactive,
  onClearRatings,
}: {
  constraint: ConstraintDirectoryEntry;
  levels: number[];
  includeInactive: boolean;
  onClearRatings: () => void;
}) {
  const { data, isLoading, isError, error, refetch } = useConstraintHolders(
    { typeKey: constraint.typeKey, valueId: constraint.id },
    levels,
    includeInactive,
  );
  // "Back to Constraints" on the person page this links to. Built from the
  // CURRENT location, so the filters in the URL come back with it.
  const originSearch = useOriginLinkSearch();

  if (isError) {
    return (
      <ErrorState message={error?.message ?? "Failed to load holders"} onRetry={() => refetch()} />
    );
  }

  const holders = data?.holders ?? [];
  // A rating filter only ranges over people whose ratings this viewer may see,
  // so for a non-admin the result is a subset by design. Say so — presenting it
  // as the whole answer would be the misleading part.
  const partial = Boolean(data?.ratingFiltered && !data.seesAllRatings);

  return (
    <div className="space-y-3 rounded-md border border-border p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h2 className="font-semibold text-lg">{constraint.name}</h2>
          <p className="text-muted-foreground text-sm">
            {constraint.typeName} · {constraint.holderCount}{" "}
            {constraint.holderCount === 1 ? "person" : "people"} familiar
          </p>
        </div>
        {levels.length > 0 && (
          <Button variant="ghost" size="sm" onClick={onClearRatings}>
            Clear rating filter
          </Button>
        )}
      </div>

      <CoverageBars constraint={constraint} />

      {partial && (
        <p className="rounded-md bg-muted/60 px-3 py-2 text-muted-foreground text-xs">
          Filtering by rating only covers people whose ratings you can see — yourself and anyone who
          reports to you. Clear the filter to see everyone who holds this.
        </p>
      )}

      {isLoading ? (
        <LoadingRows />
      ) : holders.length === 0 ? (
        <EmptyState
          icon={Users}
          title={levels.length ? "Nobody matches that rating" : "Nobody holds this yet"}
          description={
            levels.length
              ? "Try clearing the rating filter."
              : "Ratings are added on a person's profile, under Constraints."
          }
        />
      ) : (
        <ul className="divide-y divide-border">
          {holders.map((h) => (
            <li key={h.personId} className="flex items-start gap-3 py-2.5">
              <div className="min-w-0 flex-1">
                <Link
                  to="/dashboard/persons/$personId"
                  params={{ personId: h.personId }}
                  search={originSearch}
                  className="font-medium text-sm hover:underline"
                >
                  {h.personName}
                </Link>
                {h.title && <p className="truncate text-muted-foreground text-xs">{h.title}</p>}
                {/* Not truncated, unlike the title: the note is the staffing
                    context a number can't carry — "one project, three years
                    ago" — and clipping it would lose the half that matters.
                    Blanked by the server for anyone who may not see the
                    rating, so there is nothing to gate here. */}
                {h.notes && (
                  <p className="mt-0.5 text-muted-foreground text-xs italic">{h.notes}</p>
                )}
              </div>
              {h.status !== "active" && <StatusPill tone="neutral" label="Inactive" />}
              <div className="shrink-0 text-right">
                {h.level === null ? (
                  // Redacted by the server. Said plainly, because a blank would
                  // read as "unrated" — a different fact, and not a true one.
                  <span className="text-muted-foreground text-xs italic">Not shown</span>
                ) : (
                  <RatingBadge level={h.level} name={h.levelName} />
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
